import styled from 'styled-components';

const permissionBadgeColors = {
  owner: {
    bg: (theme: {
      tint?: { primary?: { light?: string } };
      palette: { action: { hover: string }; primary: { main: string } };
    }) => theme.tint?.primary?.light ?? theme.palette.action.hover,
    text: (theme: { palette: { primary: { main: string } } }) => theme.palette.primary.main,
  },
  edit: {
    bg: (theme: { tint?: { success?: { light?: string } }; palette: { action: { hover: string } } }) =>
      theme.tint?.success?.light ?? theme.palette.action.hover,
    text: (theme: { palette: { success: { main: string } } }) => theme.palette.success.main,
  },
  view: {
    bg: (theme: { tint?: { warning?: { light?: string } }; palette: { action: { hover: string } } }) =>
      theme.tint?.warning?.light ?? theme.palette.action.hover,
    text: (theme: { palette: { warning: { main: string } } }) => theme.palette.warning.main,
  },
};

export const PermissionBadgeStyled = styled.span<{ $level: 'owner' | 'edit' | 'view'; $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $level, theme }) => permissionBadgeColors[$level].bg(theme)};
  color: ${({ $level, theme }) => permissionBadgeColors[$level].text(theme)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    opacity: ${({ $clickable }) => ($clickable ? 0.85 : 1)};
  }
`;

export const StyledOption = styled.li<{ $disabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 16px;
  min-height: 36px;

  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  &:hover {
    background-color: ${(props) => (props.$disabled ? '' : props.theme.palette.grey[200])};
  }
`;

export const AlreadyAdded = styled.p`
  color: ${({ theme }) => theme.palette.text.disabled};
  line-height: 18px;
  margin: 0;
`;

export const AddPeopleRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

export const ModalDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 0 0 16px;
`;

export const ShareDialogSection = styled.div`
  margin-bottom: 8px;
`;

export const ShareDialogSectionTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  margin: 16px 0 8px;
`;

export const PeopleWithAccessList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 8px;
  padding: 0 8px;
`;

export const AccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  gap: 12px;
`;

export const AccessRowInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

export const AccessRowNameColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const AccessRowName = styled.span`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AccessRowEmail = styled.span`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const GroupRowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.tint.primary.medium};
  color: ${({ theme }) => theme.palette.info.dark};
`;

export const AccessRowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const OwnerLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
`;

export const EmptyPeopleMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  color: ${({ theme }) => theme.palette.text.disabled};
  padding: 8px 0;
`;

export const GeneralAccessSection = styled.div`
  margin-bottom: 8px;
`;

export const GeneralAccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  gap: 12px;
`;

export const GeneralAccessInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const GeneralAccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.action.hover};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const GeneralAccessLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GeneralAccessTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const GeneralAccessHint = styled.span`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const ShareLinkUrl = styled.div`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: 4px;
  word-break: break-all;
`;

export const CopyLinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
`;
